import { Injectable, Type } from '@angular/core';

import { API_META, BeehiveApiService } from '../beehive-api.service.js';
import { Application } from './application.model.js';
import {
  ApiQueryParams,
  FilterSuffix,
  mergeApiParams,
} from '../api.operators.js';
import { map, Observable } from 'rxjs';
import { ApiResponse } from '../api.interfaces.js';
import { Course } from '../course/course.model.js';
import { Camp } from '../camp/camp.model.js';
import {
  ApplicationStatus,
  OpenSpotsInformation,
} from './lib/application.interfaces.js';
import { ContractData } from '../contract/lib/contract.interfaces.js';
import { Workshop } from '../workshop/workshop.model.js';
import { ActivityRole } from '../activity/lib/activity.interfaces.js';
import { isWorkshop } from '../workshop/workshop.util';
import { isCourse } from '../course/course.util';
import {
  findStaffScheduleOverlap,
  getTargetFilter,
} from '../../util/target.util';
import { Activity } from '../activity/activity.model';
import { castDataToEntity } from '../cast-data-to-entity';

@Injectable({
  providedIn: 'root',
})
export class ApplicationService extends BeehiveApiService<Application> {
  protected readonly entityType = Application;
  protected readonly endpoint = 'applications';

  public getAvailabilities(
    rawParams?: Omit<
      ApiQueryParams<Application>,
      // Filtering, searching, and sorting not supported on availabilities endpoint.
      'filters' | 'search' | 'sorts'
    >,
  ): Observable<(Course | Camp | Workshop)[]> {
    const params = this.parseQuery(rawParams);
    const url = this.getUrl(encodeURIComponent('availabilities'));

    return this.http
      .get<
        ApiResponse<
          {
            type: 'course' | 'workshop' | 'camp';
            object: Course | Camp | Workshop;
          }[]
        >
      >(url, params)
      .pipe(
        map((response) => {
          const data = response.data.map(({ type, object }) => {
            const typeMap = { course: Course, workshop: Workshop, camp: Camp };
            return castDataToEntity(typeMap[type] as Type<any>, object);
          });
          Reflect.defineMetadata(API_META, response.meta, data);
          return data;
        }),
      );
  }

  public updateStatus(
    id: string,
    data: {
      status: ApplicationStatus;
      activityData?: {
        contractData: ContractData;
        role: ActivityRole;
      };
    },
    rawParams?: ApiQueryParams<Application>,
  ): Observable<Application> {
    const url = this.getUrl(encodeURIComponent(id));
    const params = this.parseQuery(rawParams);
    return this.http
      .patch<ApiResponse<Application>>(url, { data }, params)
      .pipe(
        map((response) => castDataToEntity(this.entityType, response.data)),
      );
  }

  public approve(
    application: Application,
    data: {
      contractData: ContractData;
      role: ActivityRole;
    },
    rawParams?: ApiQueryParams<Application>,
  ): Observable<Activity> {
    return this.updateStatus(
      application.id,
      {
        status: ApplicationStatus.Approved,
        activityData: data,
      },
      rawParams,
    ).pipe(
      map((application) => castDataToEntity(Activity, application.activity)),
    );
  }

  getStaffOverlappingApplications(
    application: Application,
    rawParams?: ApiQueryParams<Application>,
  ): Observable<Application[]> {
    return this.list(
      mergeApiParams(rawParams, {
        filters: {
          ...getTargetFilter(application, 'id'),
          'staff.id': [FilterSuffix.NOT, application.staff.id],
        },
      }),
    );
  }

  getOtherApplicationsOnSamePeriod(
    application: Application,
    rawParams?: ApiQueryParams<Application>,
  ): Observable<Application[]> {
    return findStaffScheduleOverlap(
      this,
      application.staff,
      application,
      rawParams,
    );
  }

  getOpenSpots(
    resource: Workshop | Camp | Course,
  ): Observable<OpenSpotsInformation> {
    const key = isCourse(resource)
      ? 'courses'
      : isWorkshop(resource)
        ? 'workshops'
        : 'camps';
    const url = this.getUrl('open-spots', key, encodeURIComponent(resource.id));
    return this.http
      .get<ApiResponse<OpenSpotsInformation>>(url)
      .pipe(map(({ data }) => data));
  }
}
